import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DialogActions, DialogContent } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import StandardButton from 'components/buttons/StandardButton';
import { useStyles } from 'components/dialogs/addTeamDialog/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import { BodyLarge, BodyLargeBold, H3 } from 'components/typography/Typography';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

interface DeleteTeamDialogProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onDeleteTeam: (teamId: string, eventId: string | null, teamName: string) => void;
  participant: ParticipantInterface;
}

const DeleteTeamDialog: React.FC<React.PropsWithChildren<DeleteTeamDialogProps>> = ({
  open,
  loading,
  onClose,
  onDeleteTeam,
  participant,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const classesProp = useMemo(() => ({ paper: classes.teamDialogPaper }), [classes.teamDialogPaper]);

  const [teamName, setTeamName] = useState<string>(participant?.participantName);

  const hasDraws = participant?.events?.some((event: any) =>
    event.drawDefinitions?.some((drawDefinition) =>
      drawDefinition.structures?.some(
        (structure) =>
          structure.matchUps?.length &&
          structure.positionAssignments?.some((position) => position.participantId === participant.participantId),
      ),
    ),
  );

  const deleteTeam = useCallback(() => {
    const eventId = participant?.events?.[0]?.eventId ?? null;
    onDeleteTeam(participant?.participantId, eventId, participant?.participantName);
    onClose();
  }, [participant, onDeleteTeam, onClose]);

  useEffect(() => {
    setTeamName(participant?.participantName);
  }, [participant]);

  return (
    <TMXDialog fullWidth classes={classesProp} open={open}>
      <H3>{t('teams.deleteTeam')}</H3>
      <DialogContent className={classes.dialogContent}>
        <BodyLarge className={classes.dialogContentElement}>
          <strong>{t('team')}</strong>: {teamName}
        </BodyLarge>
        {!hasDraws && <BodyLarge className={classes.dialogContentElement}>{t('teams.deleteModalText1')}</BodyLarge>}
        <BodyLarge className={classes.dialogContentElement}>
          {hasDraws ? (
            <Trans
              i18nKey="teams.deleteModalText2Draws"
              values={{
                team: teamName,
              }}
            />
          ) : (
            participant?.individualParticipantIds?.length > 0 && (
              <Trans
                components={{
                  strong: <BodyLargeBold component="span" />,
                }}
                i18nKey="teams.deleteModalText2"
                values={{
                  team: teamName,
                }}
              />
            )
          )}
        </BodyLarge>
      </DialogContent>
      <DialogActions className={classes.actionsContainer}>
        <StandardButton onClick={onClose}>{t('actions.cancel')}</StandardButton>
        {!hasDraws && (
          <StandardButton loading={loading} variant="warning" onClick={deleteTeam}>
            {t('teams.deleteTeam')}
          </StandardButton>
        )}
      </DialogActions>
    </TMXDialog>
  );
};

export default DeleteTeamDialog;
